import { Link } from "react-router-dom";

function ProductCard({
  product,
}: {
  product: {
    name: string;
    price: string;
    image: string;
    description: string;
    tag?: string;
  };
}) {
  return (
    <div className="group relative bg-[#0a0a0a] rounded-xl overflow-hidden border border-white/10 hover:border-cyan-500/30 transition-all duration-500 hover:shadow-[0_0_30px_rgba(0,255,255,0.06)]">
      {product.tag && (
        <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-cyan-500/15 border border-cyan-400/30 text-[10px] tracking-widest uppercase text-cyan-300 font-medium backdrop-blur-sm">
          {product.tag}
        </div>
      )}
      <div className="aspect-[4/5] p-6 flex items-center justify-center bg-gradient-to-br from-cyan-900/10 via-[#0a1520] to-[#0a0a0a]">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-110"
        />
      </div>
      <div className="p-5">
        <h3 className="text-white font-semibold text-lg mb-2">{product.name}</h3>
        <p className="text-gray-400 text-sm mb-4 line-clamp-3 leading-relaxed">
          {product.description}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-cyan-400 font-bold text-xl">{product.price}</span>
          <button className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-teal-600 rounded-full text-white text-sm font-medium hover:from-cyan-700 hover:to-teal-700 transition-all duration-300 shadow-lg shadow-cyan-900/30">
            Inquire
          </button>
        </div>
      </div>
    </div>
  );
}

const products = [
  {
    name: "Kth'ar Vocalizer",
    price: "$89",
    image: "/images/product-alien-spire.png",
    tag: "New Arrival",
    description:
      "Salvaged from the deep-sea wreckage of Kth'ar signal buoys. This bioluminescent resonator emits harmonic frequencies when energized — believed to be a communication relay for non-human intelligences. Do not operate near electronics.",
  },
  {
    name: "Cephalopod Array Unit",
    price: "$120",
    image: "/images/product-alien-tentacle.png",
    tag: "Rare Find",
    description:
      "A neural-interface appendage recovered from the abyssal zone. The radial suction array interfaces with electromagnetic fields. Originally designed for deep-pressure environments. Handle with extreme caution — it is partially alive.",
  },
  {
    name: "Void Tendril Specimen",
    price: "$95",
    image: "/images/product-alien-artifact.png",
    tag: "Specimen",
    description:
      "A calcified growth of unknown biological origin. Spectral analysis shows isotopic ratios not found on Earth. Emits faint bioluminescence in complete darkness. Classification pending.",
  },
  {
    name: "Abyssal Drone Core",
    price: "$45",
    image: "/images/product1.png",
    tag: "Classified",
    description:
      "Intercepted autonomous drone component from the Mariana Trench anomaly. The internal lattice structure suggests manufacturing at pressures exceeding 1,100 atm. Non-terrestrial origin rated 87% probable.",
  },
  {
    name: "Phase-Shift Catalyst",
    price: "$55",
    image: "/images/product2.png",
    tag: "Experimental",
    description:
      "Extracted from a thermal vent colony at the Mid-Atlantic Ridge. Exhibits anomalous piezoelectric properties — vibrates at frequencies that induce altered states of perception. Not for human contact.",
  },
  {
    name: "Xenolith Probe",
    price: "$65",
    image: "/images/product3.png",
    tag: "Anomalous",
    description:
      "A self-sculpting mineral composite that reshapes in response to bioelectric fields. Recovered from a subduction zone where crustal plates meet. Its purpose remains unknown. Display only.",
  },
];

export function ShopPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-400/20 text-[11px] tracking-[0.2em] uppercase text-cyan-300 font-medium">
            Classification Level: Public
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-cyan-400 via-teal-300 to-emerald-400 bg-clip-text text-transparent">
            Xenotech Archive
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
            Recovered artifacts of non-human origin. Each specimen has been catalogued,
            analyzed, and sealed by the Exobiology Division. These items are{" "}
            <span className="text-cyan-400/80">not for human use</span> — they are
            technological remnants of unknown civilizations, presented for study and
            collection.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {products.map((product) => (
            <ProductCard key={product.name} product={product} />
          ))}
        </div>

        {/* Info Section */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <div className="text-center p-6 bg-white/[0.03] rounded-xl border border-white/[0.06]">
            <div className="text-3xl mb-3">📡</div>
            <h3 className="font-semibold mb-2 text-white/90">Origin Verified</h3>
            <p className="text-gray-500 text-sm">
              Every specimen is spectrographically analyzed and geolocated to its
              recovery site
            </p>
          </div>
          <div className="text-center p-6 bg-white/[0.03] rounded-xl border border-white/[0.06]">
            <div className="text-3xl mb-3">⚠️</div>
            <h3 className="font-semibold mb-2 text-white/90">Containment Protocols</h3>
            <p className="text-gray-500 text-sm">
              Shipped in Faraday-shielded packaging with biohazard containment
              documentation
            </p>
          </div>
          <div className="text-center p-6 bg-white/[0.03] rounded-xl border border-white/[0.06]">
            <div className="text-3xl mb-3">🔐</div>
            <h3 className="font-semibold mb-2 text-white/90">Secure Dispatch</h3>
            <p className="text-gray-500 text-sm">
              Unmarked vessels. No human-readable return address. GPS-tracked to
              delivery coordinates
            </p>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-16 text-center max-w-2xl mx-auto">
          <p className="text-[11px] text-white/20 leading-relaxed tracking-wide">
            DISCLAIMER — All items in this archive are classified as non-human technological
            artifacts. The Xenotech Division makes no claims regarding the safety, function,
            or intended purpose of these specimens for any terrestrial biological entity.
            Purchasers assume all liability. By acquiring these items you acknowledge that
            you are an authorized collector and will comply with the Non-Human Artifact
            Handling Guidelines (NHAHG).
          </p>
        </div>
      </div>
    </div>
  );
}
